import React, { useState, useEffect } from 'react';
import api from '../services/api';

const InvoicePrintModal = ({
  isOpen,
  onClose,
  title = 'व्यापारी बील / Sales Invoice',
  invoiceType = 'sale', // 'sale' | 'purchase' | 'receipt'
  data = null
}) => {
  const [phone, setPhone] = useState('');
  const [copied, setCopied] = useState(false);
  const [sendingApi, setSendingApi] = useState(false);
  const [apiFeedback, setApiFeedback] = useState(null);
  const [waSettings, setWaSettings] = useState(null);
  const [sendToCustomer, setSendToCustomer] = useState(true);
  const [sendToOwner, setSendToOwner] = useState(false);
  const [ownerPhone, setOwnerPhone] = useState('');

  useEffect(() => {
    if (data) {
      let rawPhone = data.customerMobile || data.CustomerMobile || data.vendorMobile || data.SupplierMobile || data.mobile || '';
      let clean = String(rawPhone).replace(/\D/g, '');
      if (clean.length === 10) {
        clean = '91' + clean;
      }
      setPhone(clean || '91');
      setApiFeedback(null);
    }
  }, [data]);

  // Load WhatsApp settings from backend
  useEffect(() => {
    let isMounted = true;
    api.get('/whatsapp/settings')
      .then((res) => {
        if (isMounted && res.data && res.data.settings) {
          const s = res.data.settings;
          setWaSettings(s);
          setSendToCustomer(s.SendToCustomer !== 0);
          setSendToOwner(s.SendToOwner === 1);
          setOwnerPhone(s.OwnerMobile || s.firmDefaultContact || '');
        }
      })
      .catch((err) => {
        console.warn('Could not load WhatsApp settings:', err.message);
      });
    return () => { isMounted = false; };
  }, []);

  if (!isOpen || !data) return null;

  const firmName = data.firmName || data.companyName || 'Hari Ganga Fish Syndicate';
  const firmAddress = data.firmAddress || data.companyAddress || 'Sakhar Koliwada, Alibag, Raigad';
  const firmContact = data.firmContact || data.companyContact || '9881914831';

  const partyName = data.customerName || data.CustomerName || data.vendorName || data.SupplierName || 'Customer';
  const partyAddress = data.customerAddress || data.CustomerAddress || data.vendorAddress || data.SupplierAddress || '';
  const partyMobile = data.customerMobile || data.CustomerMobile || data.vendorMobile || data.SupplierMobile || phone || '';

  const billNo = data.saleNo || data.SaleNo || data.purchaseNo || data.id || data.Id || '1';
  const billDate = data.saleDate || data.SaleDate || data.purchaseDate || data.PayDate || new Date().toISOString().split('T')[0];
  const items = Array.isArray(data.items) ? data.items : [];
  const grandTotal = Number(data.GrandTotal || data.GranadTotal || data.total || data.PaidAmt || 0);
  const prevBalance = Number(data.balance || data.previousBalance || 0);
  const netPayable = invoiceType === 'sale' ? (prevBalance + grandTotal) : grandTotal;

  // Build WhatsApp text representation
  const generateWhatsAppText = () => {
    let typeLabel = 'विक्री बील / SALE BILL (CASH MEMO)';
    if (invoiceType === 'purchase') typeLabel = 'खरेदी बील / PURCHASE BILL';
    if (invoiceType === 'receipt') typeLabel = 'पेमेंट पावती / PAYMENT RECEIPT';

    let msg = `🐟 *${firmName}*\n`;
    if (firmAddress) msg += `📍 ${firmAddress}\n`;
    if (firmContact) msg += `📞 संपर्कासाठी: ${firmContact}\n`;
    msg += `----------------------------------------\n`;
    msg += `📋 *${typeLabel}*\n`;
    msg += `🔢 बील क्र. / Bill No: #${billNo}\n`;
    msg += `🗓️ तारीख / Date: ${billDate}\n`;
    msg += `👤 व्यापारी / ग्राहक: ${partyName}\n`;
    if (partyMobile) msg += `📱 मोबाईल: ${partyMobile}\n`;
    if (partyAddress) msg += `🏠 पत्ता: ${partyAddress}\n`;
    msg += `----------------------------------------\n`;

    if (items.length > 0) {
      msg += `📦 *सामान तपशील (Items List):*\n`;
      items.forEach((item, idx) => {
        const pName = item.productName || item.name || `मासा #${idx + 1}`;
        const boxes = item.noofbox ? `${item.noofbox} पेटी` : '';
        const kgBox = item.kgperbox ? `(${item.kgperbox}kg/पेटी)` : '';
        const netQty = item.net_qty || item.Qty || 0;
        const rate = item.Rate || 0;
        const itemTotal = Number(item.total || 0).toLocaleString('en-IN');

        msg += `${idx + 1}. *${pName}*\n`;
        if (boxes || kgBox) msg += `   ${boxes} ${kgBox}\n`;
        msg += `   निव्वळ वजन: ${netQty} kg @ दर: ₹${rate} = *₹${itemTotal}*\n`;
      });
      msg += `----------------------------------------\n`;
    }

    msg += `💰 *एकूण बील रक्कम (Total): ₹${grandTotal.toLocaleString('en-IN')}*\n`;
    if (prevBalance !== 0 && invoiceType === 'sale') {
      msg += `▶️ *मागील बाकी (Prev. Balance): ₹${prevBalance.toLocaleString('en-IN')}*\n`;
      msg += `⚠️ *एकूण देय बाकी (Net Payable): ₹${netPayable.toLocaleString('en-IN')}*\n`;
    }
    
    // View Online Link
    const publicUrl = window.location.origin;
    const viewUrl = `${publicUrl}/api/whatsapp/view-invoice?type=${invoiceType}&id=${billNo}`;
    msg += `----------------------------------------\n`;
    msg += `📄 *ऑनलाइन बील पहा आणि प्रिंट करा:*\n${viewUrl}\n`;
    msg += `----------------------------------------\n`;
    if (waSettings?.CustomMessage) {
      msg += `💬 ${waSettings.CustomMessage}\n`;
    } else {
      msg += `धन्यवाद! पुन्हा भेट द्या. (Thank You!)\n`;
    }

    return msg;
  };

  const handlePrint = () => {
    window.print();
  };

  // 1. Send via WhatsApp Gateway API
  const handleSendViaApi = async () => {
    if (waSettings && (waSettings.IsEnabled === 0 || waSettings.IsEnabled === false)) {
      alert('WhatsApp sender is currently turned OFF in Settings.\n\nPlease enable it in Settings -> WhatsApp Settings.');
      return;
    }

    if (!sendToCustomer && !sendToOwner) {
      alert('Please select at least one recipient (Customer or Owner).');
      return;
    }

    setSendingApi(true);
    setApiFeedback(null);

    try {
      const payload = {
        phone: phone.replace(/\D/g, ''),
        invoiceNo: billNo,
        saleNo: billNo,
        purchaseNo: billNo,
        saleId: data.id || data.Id,
        id: data.id || data.Id,
        type: invoiceType,
        partyName,
        customerName: partyName,
        customerMobile: phone,
        saleDate: billDate,
        grandTotal,
        subtotal: data.SubTotal || data.subtotal || grandTotal,
        hamali: data.HamaliCharges || data.hamali || 0,
        ice: data.IceCharges || data.ice || 0,
        transport: data.TransportCharges || data.transport || 0,
        discount: data.DiscAmount || data.discount || 0,
        paidAmt: data.PaidAmt || 0,
        previousBalance: prevBalance,
        netBalanceAfterSale: netPayable,
        items,
        sendToCustomer,
        sendToOwner,
        ownerMobile: ownerPhone,
        checkSender: true,
        messageText: generateWhatsAppText()
      };

      const res = await api.post('/whatsapp/send-invoice', payload);
      
      if (res.data && res.data.success) {
        setApiFeedback({
          type: 'success',
          msg: `✅ ${res.data.message || 'WhatsApp bill sent successfully!'}`
        });
      } else if (res.data && res.data.disabled) {
        setApiFeedback({
          type: 'warning',
          msg: '⚠️ WhatsApp sender is turned OFF in Settings.'
        });
      } else {
        throw new Error(res.data?.message || 'Failed to dispatch WhatsApp');
      }
    } catch (err) {
      console.error('WhatsApp API dispatch error:', err);
      const errMsg = err.response?.data?.message || err.message || 'Error communicating with WhatsApp Gateway';
      setApiFeedback({
        type: 'danger',
        msg: `❌ ${errMsg}`
      });
      if (window.confirm(`${errMsg}\n\nWould you like to open WhatsApp Web / App directly instead?`)) {
        handleSendWhatsAppWeb();
      }
    } finally {
      setSendingApi(false);
    }
  };

  // 2. Open WhatsApp Web or Mobile WhatsApp directly
  const handleSendWhatsAppWeb = () => {
    let clean = phone.replace(/\D/g, '');
    if (clean.length === 10) clean = '91' + clean;

    const targets = [];
    if (sendToCustomer && clean && clean.length >= 10) {
      targets.push(clean);
    }
    if (sendToOwner && ownerPhone) {
      const cleanOwner = ownerPhone.replace(/\D/g, '');
      const fullOwner = cleanOwner.length === 10 ? '91' + cleanOwner : cleanOwner;
      if (!targets.includes(fullOwner) && fullOwner.length >= 10) {
        targets.push(fullOwner);
      }
    }

    if (targets.length === 0) {
      if (clean && clean.length >= 10) {
        targets.push(clean);
      } else {
        alert('Please enter a valid 10-digit mobile number.');
        return;
      }
    }

    const text = generateWhatsAppText();
    const encoded = encodeURIComponent(text);

    targets.forEach((targetPhone, idx) => {
      const url = `https://api.whatsapp.com/send?phone=${targetPhone}&text=${encoded}`;
      if (idx === 0) {
        window.open(url, '_blank');
      } else {
        setTimeout(() => {
          window.open(url, '_blank');
        }, 1000);
      }
    });
  };

  const handleCopyText = () => {
    const text = generateWhatsAppText();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1060 }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
          {/* MODAL HEADER (WEB ONLY) */}
          <div className="modal-header bg-primary text-white py-3 px-4 d-print-none">
            <h5 className="modal-title fw-bold mb-0 d-flex align-items-center">
              <i className="bi bi-printer me-2"></i> {title}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          {/* ACTION BAR (WEB ONLY) */}
          <div className="p-3 bg-light border-bottom d-print-none">
            {apiFeedback && (
              <div className={`alert alert-${apiFeedback.type} alert-dismissible py-2 px-3 mb-2 small shadow-sm`} role="alert">
                <div className="d-flex justify-content-between align-items-center">
                  <span>{apiFeedback.msg}</span>
                  <button type="button" className="btn-close btn-close-sm" onClick={() => setApiFeedback(null)}></button>
                </div>
              </div>
            )}

            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-5">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white fw-bold text-success">
                    <i className="bi bi-whatsapp me-1"></i> +
                  </span>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="919881914831 (Country code + Mobile)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                {/* Multi-recipient checkboxes */}
                <div className="d-flex gap-3 mt-1">
                  <div className="form-check form-check-inline mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="chkCustomer"
                      checked={sendToCustomer}
                      onChange={(e) => setSendToCustomer(e.target.checked)}
                    />
                    <label className="form-check-label small text-muted" htmlFor="chkCustomer">
                      Customer
                    </label>
                  </div>
                  <div className="form-check form-check-inline mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="chkOwner"
                      checked={sendToOwner}
                      onChange={(e) => setSendToOwner(e.target.checked)}
                    />
                    <label className="form-check-label small text-muted" htmlFor="chkOwner" title={ownerPhone ? `Owner: ${ownerPhone}` : 'Owner'}>
                      Firm Owner {ownerPhone ? `(${ownerPhone})` : ''}
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-7 d-flex gap-2 justify-content-md-end flex-wrap">
                {waSettings?.SendingMethod === 'web' ? (
                  <>
                    {/* Send via WhatsApp Web / App directly (PRIMARY) */}
                    <button
                      type="button"
                      className="btn btn-success btn-sm rounded-pill px-3 shadow-sm d-flex align-items-center gap-1 fw-bold"
                      onClick={handleSendWhatsAppWeb}
                      title="Open in WhatsApp Web / App (Default in Settings)"
                    >
                      <i className="bi bi-whatsapp"></i>
                      <span>वेब / ॲप (Web)</span>
                      <span className="badge bg-white text-success ms-1">मुख्य</span>
                    </button>

                    {/* Send via WhatsApp Gateway API (SECONDARY) */}
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm rounded-pill px-3 shadow-sm d-flex align-items-center gap-1"
                      onClick={handleSendViaApi}
                      disabled={sendingApi}
                      title="Send dynamically generated PDF invoice via WhatsApp Gateway"
                    >
                      {sendingApi ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                          <span>पाठवत आहे...</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send-fill"></i>
                          <span>व्हॉट्सॲप पाठवा (API)</span>
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Send via WhatsApp Gateway API (PRIMARY) */}
                    <button
                      type="button"
                      className="btn btn-success btn-sm rounded-pill px-3 shadow-sm d-flex align-items-center gap-1 fw-bold"
                      onClick={handleSendViaApi}
                      disabled={sendingApi}
                      title="Send dynamically generated PDF invoice via WhatsApp Gateway (Default in Settings)"
                    >
                      {sendingApi ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                          <span>पाठवत आहे...</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send-fill"></i>
                          <span>व्हॉट्सॲप पाठवा (API)</span>
                          <span className="badge bg-white text-success ms-1">मुख्य</span>
                        </>
                      )}
                    </button>

                    {/* Send via WhatsApp Web / App directly (SECONDARY) */}
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm rounded-pill px-3 shadow-sm d-flex align-items-center gap-1"
                      onClick={handleSendWhatsAppWeb}
                      title="Open in WhatsApp Web / App"
                    >
                      <i className="bi bi-whatsapp"></i>
                      <span>वेब / ॲप (Web)</span>
                    </button>
                  </>
                )}

                {/* Copy Text */}
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill px-3 shadow-sm"
                  onClick={handleCopyText}
                >
                  <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-clipboard'} me-1`}></i>
                  {copied ? 'कॉपी झाले!' : 'मजकूर कॉपी'}
                </button>

                {/* Print */}
                <button
                  type="button"
                  className="btn btn-dark btn-sm rounded-pill px-3 shadow-sm"
                  onClick={handlePrint}
                >
                  <i className="bi bi-printer me-1"></i> प्रिंट करा
                </button>
              </div>
            </div>
          </div>

          {/* PRINTABLE BILL CANVAS */}
          <div className="modal-body p-4 bg-white" id="invoice-printable-area">
            {/* INVOICE CARD */}
            <div className="border border-dark p-3 rounded-2 invoice-box" style={{ minHeight: '500px' }}>
              
              {/* FIRM HEADER */}
              <div className="text-center pb-3 border-bottom border-dark position-relative">
                <div className="badge bg-primary position-absolute start-0 top-0 d-print-none">
                  {invoiceType === 'sale' ? 'विक्री बील' : invoiceType === 'purchase' ? 'खरेदी बील' : 'पेमेंट पावती'}
                </div>
                <h2 className="fw-bold text-uppercase mb-1 tracking-wide" style={{ letterSpacing: '1px' }}>
                  {firmName}
                </h2>
                <div className="fw-semibold text-secondary small">{firmAddress}</div>
                {firmContact && (
                  <div className="text-dark small fw-bold mt-1">
                    📞 फोन / मो.: {firmContact}
                  </div>
                )}
                <div className="text-uppercase small fw-bold text-decoration-underline mt-2">
                  {invoiceType === 'sale' ? 'विक्री पावती / CASH MEMO' : invoiceType === 'purchase' ? 'खरेदी बील / PURCHASE INVOICE' : 'पेमेंट पावती / RECEIPT'}
                </div>
              </div>

              {/* INVOICE META ROW */}
              <div className="row g-2 py-2 border-bottom border-dark small">
                <div className="col-7">
                  <div><strong>नाव (Name):</strong> <span className="fs-6 fw-bold">{partyName}</span></div>
                  {partyAddress && <div><strong>पत्ता (Address):</strong> {partyAddress}</div>}
                  {partyMobile && <div><strong>मोबाईल (Mobile):</strong> {partyMobile}</div>}
                </div>
                <div className="col-5 text-end">
                  <div><strong>बील क्र. (Bill No):</strong> <span className="fw-bold fs-6">#{billNo}</span></div>
                  <div><strong>तारीख (Date):</strong> {billDate}</div>
                  {data.vehicleNo && <div><strong>गाडी क्र.:</strong> {data.vehicleNo}</div>}
                </div>
              </div>

              {/* ITEMS TABLE */}
              {items.length > 0 ? (
                <div className="table-responsive my-2">
                  <table className="table table-bordered border-dark table-sm mb-0 align-middle">
                    <thead className="table-light text-center small border-dark">
                      <tr>
                        <th style={{ width: '5%' }}>क्र.</th>
                        <th style={{ width: '40%' }} className="text-start">माशाचे नाव (Item)</th>
                        <th style={{ width: '15%' }}>पेटी (Boxes)</th>
                        <th style={{ width: '15%' }}>वजन (Net Wt)</th>
                        <th style={{ width: '10%' }}>दर (Rate)</th>
                        <th style={{ width: '15%' }} className="text-end">रक्कम (Amount)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index} className="small">
                          <td className="text-center">{index + 1}</td>
                          <td className="fw-semibold">{item.productName || item.name || 'Fish Item'}</td>
                          <td className="text-center">
                            {item.noofbox ? `${item.noofbox} ${item.kgperbox ? `(${item.kgperbox}k)` : ''}` : '-'}
                          </td>
                          <td className="text-center fw-bold">
                            {parseFloat(item.net_qty || item.Qty || 0).toFixed(2)} kg
                          </td>
                          <td className="text-center">₹{parseFloat(item.Rate || 0).toFixed(2)}</td>
                          <td className="text-end fw-bold">₹{Number(item.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                      {/* Fill empty rows for clean paper format */}
                      {items.length < 3 && Array.from({ length: 3 - items.length }).map((_, idx) => (
                        <tr key={`empty-${idx}`} style={{ height: '24px' }}>
                          <td></td><td></td><td></td><td></td><td></td><td></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-4 text-center text-muted">
                  <h5>रक्कम तपशील: ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h5>
                  {data.Remark && <p className="mb-0">टीप: {data.Remark}</p>}
                </div>
              )}

              {/* CALCULATION / SUMMARY SECTION */}
              <div className="row g-2 pt-2 border-top border-dark small">
                <div className="col-6">
                  <div className="border border-secondary p-2 rounded bg-light h-100">
                    <div className="fw-bold mb-1">टीप व अटी (Terms & Notes):</div>
                    <ol className="ps-3 mb-0" style={{ fontSize: '0.8rem' }}>
                      <li>विकलेला माल परत घेतला जाणार नाही.</li>
                      <li>सर्व व्यवहार अलबाग कार्यक्षेत्रात येतील.</li>
                      <li>बील रक्कम लवकरात लवकर जमा करावी.</li>
                    </ol>
                    {data.Remark && (
                      <div className="mt-2 text-primary fw-bold" style={{ fontSize: '0.8rem' }}>
                        रिमार्क: {data.Remark}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-6">
                  <table className="table table-sm table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td className="text-end fw-semibold">चालू बील रक्कम (Total):</td>
                        <td className="text-end fw-bold" style={{ width: '110px' }}>
                          ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                      {prevBalance !== 0 && invoiceType === 'sale' && (
                        <tr>
                          <td className="text-end text-danger fw-semibold">मागील बाकी (Prev Balance):</td>
                          <td className="text-end text-danger fw-bold">
                            ₹{prevBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      )}
                      <tr className="border-top border-dark">
                        <td className="text-end fw-bold fs-6">एकूण देय बाकी (Net Due):</td>
                        <td className="text-end fw-bold fs-6 text-primary">
                          ₹{netPayable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SIGNATURES */}
              <div className="row mt-4 pt-4 text-center small text-secondary">
                <div className="col-4">
                  <div className="border-top border-secondary pt-1">ग्राहकाची स्वाक्षरी</div>
                </div>
                <div className="col-4">
                  <div className="pt-1 fst-italic">धन्यवाद! पुन्हा भेट द्या.</div>
                </div>
                <div className="col-4">
                  <div className="border-top border-secondary pt-1 fw-bold text-dark">
                    करिता {firmName}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* MODAL FOOTER */}
          <div className="modal-footer bg-light py-2 px-3 d-print-none">
            <div className="small text-muted me-auto">
              <i className="bi bi-info-circle me-1"></i>
              बील व्हॉट्सॲपवर पाठवताना ग्राहकाला किंवा मालकाला दोन्ही पर्याय उपलब्ध आहेत.
            </div>
            <button type="button" className="btn btn-secondary btn-sm px-4" onClick={onClose}>
              बंद करा (Close)
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default InvoicePrintModal;
